import { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Upload, 
  File, 
  FileText, 
  Image as ImageIcon, 
  Loader2, 
  CheckCircle,
  AlertTriangle,
  X,
  Download,
  Eye,
  Sparkles
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface AttachmentUploadProps {
  companyId: string;
  configItemId?: string;
  messageId?: string;
  accessToken: string;
  onUploadComplete?: (attachment: any) => void;
  analyzeWithAI?: boolean;
}

interface UploadState {
  file: File | null;
  uploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
  attachment: any | null;
}

export function AttachmentUpload({
  companyId,
  configItemId,
  messageId,
  accessToken,
  onUploadComplete,
  analyzeWithAI = false
}: AttachmentUploadProps) {
  const [uploadState, setUploadState] = useState<UploadState>({
    file: null,
    uploading: false,
    progress: 0,
    error: null,
    success: false,
    attachment: null
  });

  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file size (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadState({
        ...uploadState,
        error: 'File too large. Maximum size is 50MB',
        file: null
      });
      return;
    }

    setUploadState({
      file,
      uploading: false,
      progress: 0,
      error: null,
      success: false,
      attachment: null
    });
  };

  const handleUpload = async () => {
    if (!uploadState.file) return;

    setUploadState(prev => ({ ...prev, uploading: true, progress: 0, error: null }));

    try {
      const formData = new FormData();
      formData.append('file', uploadState.file);
      formData.append('analyzeWithAI', analyzeWithAI.toString());
      
      if (configItemId) {
        formData.append('configItemId', configItemId);
      }
      if (messageId) {
        formData.append('messageId', messageId);
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadState(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90)
        }));
      }, 300);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/upload-attachment/${companyId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData
        }
      );

      clearInterval(progressInterval);

      if (response.ok) {
        const result = await response.json();
        setUploadState({
          file: uploadState.file,
          uploading: false,
          progress: 100,
          error: null,
          success: true,
          attachment: result.attachment
        });

        if (result.storageWarning) {
          console.warn('Storage warning:', result.storageWarning);
        }

        if (onUploadComplete) {
          onUploadComplete(result.attachment);
        }
      } else {
        const error = await response.json();
        setUploadState({
          ...uploadState,
          uploading: false,
          progress: 0,
          error: error.error || 'Upload failed'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadState({
        ...uploadState,
        uploading: false,
        progress: 0,
        error: 'Upload failed. Please try again.'
      });
    }
  };

  const clearFile = () => {
    setUploadState({
      file: null,
      uploading: false,
      progress: 0,
      error: null,
      success: false,
      attachment: null
    });
  };

  const getFileIcon = (file: File) => {
    const type = file.type;
    if (type.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (type.includes('pdf')) return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload Attachment
          {analyzeWithAI && (
            <Badge variant="secondary" className="ml-2">
              <Sparkles className="h-3 w-3 mr-1" />
              AI Analysis
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {analyzeWithAI 
            ? 'Upload files and let AI analyze data, extract workflows, and suggest mappings'
            : 'Upload documents, images, or data files (Max 50MB)'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Alert */}
        {uploadState.error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{uploadState.error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        {uploadState.success && uploadState.attachment && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              File uploaded.
              {uploadState.attachment.aiAnalysis && (
                <div className="mt-2">
                  <strong>AI Analysis:</strong> {uploadState.attachment.aiAnalysis.summary}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Drop Zone */}
        {!uploadState.file && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
            `}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-2">
              Drag and drop a file here, or click to browse
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Supports: Images, PDFs, CSV, Excel, JSON, XML
            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileInput}
              accept="image/*,.pdf,.csv,.xlsx,.xls,.json,.xml"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              Browse Files
            </Button>
          </div>
        )}

        {/* Selected File */}
        {uploadState.file && !uploadState.success && (
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                {getFileIcon(uploadState.file)}
                <div>
                  <p className="font-medium">{uploadState.file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(uploadState.file.size)}
                  </p>
                </div>
              </div>
              {!uploadState.uploading && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {uploadState.uploading && (
              <div className="space-y-2">
                <Progress value={uploadState.progress} className="w-full" />
                <p className="text-sm text-muted-foreground text-center">
                  {analyzeWithAI ? 'Uploading and analyzing...' : 'Uploading...'} {uploadState.progress}%
                </p>
              </div>
            )}

            {!uploadState.uploading && (
              <Button
                onClick={handleUpload}
                className="w-full mt-2"
              >
                <Upload className="h-4 w-4 mr-2" />
                {analyzeWithAI ? 'Upload & Analyze with AI' : 'Upload File'}
              </Button>
            )}
          </div>
        )}

        {/* Uploaded File Info */}
        {uploadState.success && uploadState.attachment && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {uploadState.attachment.fileType.startsWith('image/') ? (
                  <ImageIcon className="h-5 w-5" />
                ) : (
                  <FileText className="h-5 w-5" />
                )}
                <div>
                  <p className="font-medium">{uploadState.attachment.fileName}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(uploadState.attachment.fileSize)}
                  </p>
                </div>
              </div>
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>

            {uploadState.attachment.signedUrl && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(uploadState.attachment.signedUrl, '_blank')}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = uploadState.attachment.signedUrl;
                    a.download = uploadState.attachment.fileName;
                    a.click();
                  }}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={clearFile}
              className="w-full"
            >
              Upload Another File
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
